import {
  Button,
  Heading,
  SimpleGrid,
  VStack,
  Text,
  HStack,
  Container,
  Center,
  Spinner,
} from "@chakra-ui/react";
import UnselectedBearsConfirmPopup from "components/UnselectedBearsConfirmPopup";
import FancyBearsEmptyMessage from "components/FancyBearsEmptyMessage";
import useAssets from "hooks/useAssets";
import useJarsToMintByBearsCount from "hooks/useJarsToMintByBearsCount";
import useMint from "hooks/useMint";
import useTranslate from "hooks/useTranslate";
import { useCallback, useEffect, useMemo, useState } from "react";
import FancyBearCard from "./FancyBearCard";

const FancyBearsContainer = () => {
  const translate = useTranslate();
  const {
    fancyBearsInWallet,
    readFancyBearsClaimStatusByAddress,
    readHoneyJarsInWallet,
  } = useAssets();
  const [tokensToClaim, setTokensToClaim] = useState<number[]>();
  const jarsToMintCount = useJarsToMintByBearsCount(tokensToClaim?.length);
  const [isUnselectedPopupOpen, setIsUnselectedPopupOpen] = useState(false);

  const availableToClaim = useMemo(() => {
    if (fancyBearsInWallet) {
      return fancyBearsInWallet.filter(([tokenId, isClaimed]) => !isClaimed);
    }
  }, [fancyBearsInWallet]);

  const handleMintSuccess = useCallback(() => {
    setTokensToClaim([]);
    readFancyBearsClaimStatusByAddress();
    readHoneyJarsInWallet();
  }, [
    setTokensToClaim,
    readFancyBearsClaimStatusByAddress,
    readHoneyJarsInWallet,
  ]);

  const [mint, isMinting] = useMint(tokensToClaim, handleMintSuccess);

  const handleClaim = useCallback(() => {
    if (!availableToClaim?.length || !tokensToClaim?.length) {
      return;
    }

    if (availableToClaim?.length > tokensToClaim?.length) {
      setIsUnselectedPopupOpen(true);
    } else {
      mint();
    }
  }, [mint, setIsUnselectedPopupOpen, availableToClaim, tokensToClaim]);

  const handleConfirm = useCallback(() => {
    setIsUnselectedPopupOpen(false);
    mint();
  }, [mint, setIsUnselectedPopupOpen]);

  const handleCancel = useCallback(() => {
    setIsUnselectedPopupOpen(false);
  }, [setIsUnselectedPopupOpen]);

  useEffect(() => {
    if (fancyBearsInWallet?.length && !tokensToClaim) {
      const availableBearsToClaim = fancyBearsInWallet
        .filter(([, isClaimed]) => !isClaimed)
        .map(([tokenId]) => tokenId);

      setTokensToClaim(availableBearsToClaim);
    }
  }, [fancyBearsInWallet, tokensToClaim]);

  if (fancyBearsInWallet === undefined) {
    return (
      <Center h="52">
        <Spinner size="xl" color="primary.500" thickness="4px" />
      </Center>
    );
  }

  if (fancyBearsInWallet && !fancyBearsInWallet.length) {
    return (
      <Container pt="8" maxW="sm">
        <FancyBearsEmptyMessage />
      </Container>
    );
  }

  return (
    <>
      <Container mt="0">
        <VStack spacing="6" w="full">
          <HStack justify="stratch" w="full" gap="4">
            <VStack w="full" align="start" spacing="0.5">
              <Heading fontSize="2xl" fontWeight="800">
                {translate("fancyBears:title")}
              </Heading>
              <Text>{translate("fancyBears:description")}</Text>
            </VStack>
          </HStack>

          <Button
            onClick={handleClaim}
            isDisabled={!jarsToMintCount}
            isLoading={isMinting}
            size="lg"
            w="full"
          >
            <>
              {jarsToMintCount !== undefined && jarsToMintCount > 0
                ? jarsToMintCount > 1
                  ? translate("common:claim:multi", { count: jarsToMintCount })
                  : translate("common:claim:one")
                : translate("common:claim:none")}

              <Text fontWeight="500" ml="2">
                {translate("fancyBears:selected", {
                  count: tokensToClaim?.length ?? 0,
                })}
              </Text>
            </>
          </Button>

          <SimpleGrid columns={2} spacing="4" w="full">
            {fancyBearsInWallet.map(([tokenId, isClaimed]) => {
              const isSelected = !!tokensToClaim?.includes(tokenId);

              return (
                <FancyBearCard
                  key={tokenId}
                  tokenId={tokenId}
                  isDisabled={isClaimed}
                  isSelected={isSelected}
                  isBusy={isSelected && isMinting!}
                  badgeText={
                    isClaimed ? translate("common:claimed") : undefined
                  }
                  onClick={() => {
                    if (isMinting) {
                      return;
                    }

                    if (isSelected) {
                      setTokensToClaim(
                        tokensToClaim?.filter((id) => tokenId !== id)
                      );
                    } else {
                      setTokensToClaim([...(tokensToClaim || []), tokenId]);
                    }
                  }}
                />
              );
            })}
          </SimpleGrid>
        </VStack>
      </Container>

      <UnselectedBearsConfirmPopup
        selectedCount={tokensToClaim?.length}
        isOpen={isUnselectedPopupOpen}
        onClose={handleCancel}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default FancyBearsContainer;
